import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityFileTypeEnum,
  ActivityStatusEnum,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';

import { combineLatest, forkJoin, of, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { TabBarLinkInterface } from '~/component/router-tab-bar/interfaces/tab-link.interface';
import { Activity } from '~/core/model/activity.model';
import { ReviewStat } from '~/core/model/review-stats.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { TitleService } from '~/core/services/title/title.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { OrganizerActivityStatusEnum } from '~/organizer/enums/activity-status.enum';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { HeaderVariantEnum } from '~/component/header/enums/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { LoaderService } from '~/core/services/loader/loader.service';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { ViewportScroller } from '@angular/common';

const startDate = new Date();
const filterStatusByType = {
  draft: 'DRAFT' as ActivityStatusEnum,
  published: 'PUBLISHED' as ActivityStatusEnum,
  unpublished: 'PUBLISHED' as ActivityStatusEnum,
};

const filterTimeslotsByType = {
  published: {
    startDate: startDate.toISOString(),
    timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
  },
};

const filterPlannable = {
  unpublished: true,
};

@Component({
  selector: 'app-organizer-activity-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class OrganizerSharedPageListComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly addIconType = VtmnPlayIconTypeEnum.Add;
  public readonly buttonIconType = VtmnPlayIconTypeEnum.ChevronRight;
  public readonly infoIconType = VtmnPlayIconTypeEnum.Information;
  public readonly linkVariant = CtaVariantEnum.Link2;
  public readonly organizerSubscriptionLoaderKey =
    'ORGANIZER_SUBSCRIPTION_LOADER_KEY';
  public readonly tabBarlinks: TabBarLinkInterface[] = [];
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  public activities: Activity[] = [];
  public activitiesReviewsStats: {
    reviewStat: ReviewStat;
    sku: number | string;
  }[] = [];
  public activityTypeEnum = OrganizerActivityStatusEnum;
  public hasSubscriptionError = false;
  public isLoading = false;
  public p = 1;
  public type: OrganizerActivityStatusEnum;

  constructor(
    private readonly loaderService: LoaderService,
    private readonly localeService: LocaleService,
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: ActivityService,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.organizer.activity.list.title');
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(
          this.organizerSubscriptionLoaderKey
        );
      })
    );
    this.loaderService.start(this.organizerSubscriptionLoaderKey);

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap((data) => {
            this.type = data.type;

            return this.organizerSharedActivityService.getActivitiesByOrganizationId(
              this.organizerSharedOrganizationService.organization.identifier,
              filterTimeslotsByType?.[this.type],
              filterStatusByType[this.type],
              filterPlannable?.[this.type]
            );
          }),
          mergeMap((data) => {
            if (
              this.type === this.activityTypeEnum.DRAFT ||
              HeaderVariantEnum.Organizer
            ) {
              return combineLatest([of(data.items)]);
            }

            const activitiesReviewsStats = forkJoin(
              data.items.map(({ sku }) =>
                this.activityService
                  .getActivityReviewsStats(sku, { disableRetry: true })
                  .pipe(map((reviewStat) => ({ reviewStat, sku })))
              )
            );
            return combineLatest([of(data.items), activitiesReviewsStats]);
          })
        )
        .subscribe({
          next: ([activities, activitiesReviewsStats]) => {
            this.loaderService.stop(this.organizerSubscriptionLoaderKey);
            this.activities =
              activities.slice().sort((a, b) => (a.sku > b.sku ? -1 : 1)) || [];
            this.activitiesReviewsStats = activitiesReviewsStats || [];
          },
          error: () => {
            this.hasSubscriptionError = true;
            this.loaderService.stop(this.organizerSubscriptionLoaderKey);
          },
        })
    );
  }

  public get dateLocale(): Locale {
    return this.localeService.dateLocale;
  }

  public getActivityReviewsStats(sku: number): ReviewStat {
    return this.activitiesReviewsStats.find(
      (activityReviewStat) => activityReviewStat.sku === sku
    )?.reviewStat;
  }

  public getPicture(activity: Activity): string {
    return (
      activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || activity.picture
    );
  }

  public changePage($event): void {
    this.p = $event;
    this.viewportScroller.scrollToPosition([0, 0]);
  }
}
